import dateFormat from "dateformat";

import {
  Company,
  DispositionType,
  EDB,
  PreparationOrder,
  ShippingAddress,
  ShippingAddressEntityType,
  ShippingRecord,
  ShippingFileType,
  ShippingRecordStatus,
  DeliveryType,
  ProofOfDeliveryType,
} from "$types";
import { UserRoles } from "$utils/typeConstants/User";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";

import { ACTIVITIES_LABELS } from "./Activity";

export const SHIPPING_RECORD_STATUS_LABELS: {
  [key in UserRoles]: { [key in ShippingRecordStatus]: string };
} = {
  [UserRoles.ADMIN]: {
    [ShippingRecordStatus.DRAFT]: "Brouillon",
    [ShippingRecordStatus.TO_SHIP]: "A expédier",
    [ShippingRecordStatus.SHIPPED]: "Expédié",
    [ShippingRecordStatus.DELIVERED]: "Livré",
    [ShippingRecordStatus.COLLECTED]: "Collecté",
    [ShippingRecordStatus.CANCELED]: "Annulé",
  },
  [UserRoles.USER]: {
    [ShippingRecordStatus.DRAFT]: "Brouillon",
    [ShippingRecordStatus.TO_SHIP]: "A expédier",
    [ShippingRecordStatus.SHIPPED]: "Expédié",
    [ShippingRecordStatus.DELIVERED]: "Livré",
    [ShippingRecordStatus.COLLECTED]: "Collecté",
    [ShippingRecordStatus.CANCELED]: "Annulé",
  },
};

// see src/index.tsx for the available colors
const SHIPPING_RECORD_STATUS_COLORS: {
  [key in ShippingRecordStatus]: string;
} = {
  [ShippingRecordStatus.DRAFT]: "blueSapphire",
  [ShippingRecordStatus.TO_SHIP]: "silverPale",
  [ShippingRecordStatus.SHIPPED]: "pinkMountbatten",
  [ShippingRecordStatus.DELIVERED]: "redShimmering",
  [ShippingRecordStatus.COLLECTED]: "yellowOrange",
  [ShippingRecordStatus.CANCELED]: "gray3",
};
export const getShippingRecordStatusColor = (
  status: ShippingRecordStatus,
  theme: any
): string => theme.color[SHIPPING_RECORD_STATUS_COLORS[status]];

export const SHIPPING_RECORD_FIELDS_TO_WATCH_LABELS = {
  wishedDeliveryDate: `Date de livraison souhaitée`,
  wishedShippingDate: `Date d'enlèvement souhaitée`,
  expectedDeliveryDate: `Date de livraison prévue`,
  carrierReference: `Référence transporteur`,
  carrierTrackingLink: `Lien de suivi transporteur`,
  agencyCompanyId: `Agence`,
  carrierCompanyId: `Transporteur`,
  commissionCompanyId: `Commissionnaire`,
  deliveryType: `Type de livraison`,
  notes: `Notes`,
  price: `Tarif`,
  withGasoil: `Gazole`,
  status: `Statut`,
  files: "Documents",
};

export const DELIVERY_TYPE_LABELS = {
  [DeliveryType.CHARTERING]: "Affrètement",
  [DeliveryType.COURIER]: "Coursier",
  [DeliveryType.EXPRESS]: "Express",
  [DeliveryType.MESSAGING]: "Messagerie",
  [DeliveryType.PROVISIONING]: "Mise à disposition",
};

export const PROOF_OF_DELIVERY_TYPE_LABELS = {
  [ProofOfDeliveryType.RECEIVED]: "Reçue",
  [ProofOfDeliveryType.NOT_RECEIVED]: "Non reçue",
};

export const ACTIVITY_DELIVERY_TYPE_MAP = {
  chartering: DeliveryType.CHARTERING,
  courier: DeliveryType.COURIER,
  express: DeliveryType.EXPRESS,
  messaging: DeliveryType.MESSAGING,
};

export const getShippingRecordHistoryFieldLabel = (key: string): string => {
  if (key === ShippingFileType.PROOF_OF_DELIVERY) {
    return "Preuve de livraison";
  }
  if (key === ShippingFileType.LABEL) {
    return "Etiquette";
  }
  if (key === ShippingFileType.MANIFEST) {
    return "Bordereau";
  }
  return SHIPPING_RECORD_FIELDS_TO_WATCH_LABELS[key];
};

export const formatHistoryFieldValue = (
  key: string,
  value: any,
  companies: Company[],
  userRole: any,
  isOldValue?: boolean
): string => {
  switch (key) {
    case "wishedDeliveryDate":
    case "wishedShippingDate":
    case "expectedDeliveryDate":
      return value ? dateFormat(value, DEFAULT_DATE_FORMAT) : "N/A";

    case "agencyCompanyId":
    case "carrierCompanyId":
    case "commissionCompanyId":
      return companies.find((company) => company._id === value)?.name || "N/A";

    case "status":
      return SHIPPING_RECORD_STATUS_LABELS[userRole][value] || "N/A";

    case "deliveryType":
      return ACTIVITIES_LABELS[value.toLowerCase()] || "N/A";

    case "withGasoil":
      return value === "true" ? "Avec gazole" : "Hors gazole";

    case "files":
      return isOldValue ? value || "N/A" : value || "Supprimé";

    case ShippingFileType.PROOF_OF_DELIVERY:
      return value ? "Reçue" : "Non reçue";

    default:
      return value || "N/A";
  }
};

export const getShippingRecordStatusLabelByRoleAndStatus = (
  userRole: UserRoles,
  status: ShippingRecordStatus
): string => SHIPPING_RECORD_STATUS_LABELS[userRole][status];

export const getShippingRecordDeliveryTypeLabel = (
  deliveryType: DeliveryType
): string => DELIVERY_TYPE_LABELS[deliveryType];

export const getDeliveryTypeOptions = () =>
  Object.keys(DELIVERY_TYPE_LABELS).map((key) => ({
    value: key,
    label: DELIVERY_TYPE_LABELS[key],
  }));

export const getShippingRecordStatusLabelOptions = (
  userRole: UserRoles,
  filterStatuses: ShippingRecordStatus[] = []
): {
  value: ShippingRecordStatus;
  label: string;
}[] => {
  return Object.values(ShippingRecordStatus)
    .filter((status) => !filterStatuses.includes(status))
    .map((status) => ({
      value: status,
      label: getShippingRecordStatusLabel(userRole, status),
    }));
};

export const getShippingRecordStatusLabel = (
  userRole: UserRoles,
  shippingRecordStatus: ShippingRecordStatus
): string => SHIPPING_RECORD_STATUS_LABELS[userRole][shippingRecordStatus];

export const getShippingRecordProofOfDeliveryLabel = (
  proofOfDeliveryType: ProofOfDeliveryType
): string => PROOF_OF_DELIVERY_TYPE_LABELS[proofOfDeliveryType];

export const getShippingRecordStatusLabelByRole = (
  userRole: UserRoles,
  shippingRecord: ShippingRecord
): string => SHIPPING_RECORD_STATUS_LABELS[userRole][shippingRecord?.status];

export const getShippingLocationLabelFromAddress = (
  shippingRecord: ShippingRecord
): string => {
  if (!shippingRecord || !shippingRecord.shipments) {
    return "N/A";
  }
  return `${shippingRecord.shipments[0]?.fromAddress.name} ${shippingRecord.shipments[0]?.fromAddress.zip} ${shippingRecord.shipments[0]?.fromAddress.city}`;
};

export const getDeliveryLocationLabelFromAddress = (
  recipientAddress
): string => {
  return `${recipientAddress?.name} ${recipientAddress?.zip} ${recipientAddress?.city}`;
};

export const createShippingAddressFromOp = (
  op: PreparationOrder
): Partial<ShippingAddress> => ({
  name: op.entrepot.name || "",
  street1: op.entrepot.location?.adress,
  street2: op.entrepot.location?.complement,
  zip: op.entrepot.location?.cp,
  city: op.entrepot.location?.city,
  country: "FR",
  phone: op?.edb?.phoneContactRecovery,
  email: op?.edb?.emailContactRecovery,
  entityType: ShippingAddressEntityType.WAREHOUSE,
  entityId: op.entrepot._id,
});

export const createDeliveryAddressFromOp = (
  op: PreparationOrder
): Partial<ShippingAddress> => {
  switch (op.shipType) {
    case DispositionType.ON_DOCKYARD:
      return {
        name: op.chantier?.name ?? op.dockyard?.name,
        street1: op.shipAdress,
        street2: op.shipAdress2,
        zip: op.shipPostalCode,
        city: op.shipCity,
        country: "FR", // En dur pour gérer shippypro pour le moment
        phone: op?.edb?.phoneContactRecovery,
        email: op?.edb?.emailContactRecovery,
        entityType: ShippingAddressEntityType.DOCKYARD,
        entityId: op.chantier?._id,
      };
    case DispositionType.OTHER_PLACE:
      return {
        name: op.shipPlace,
        street1: op.shipAdress,
        street2: op.shipAdress2,
        zip: op.shipPostalCode,
        city: op.shipCity,
        country: "FR",
        phone: op?.edb?.phoneContactRecovery,
        email: op?.edb?.emailContactRecovery,
        entityType: ShippingAddressEntityType.OTHER,
        entityId: op.shipPlace,
      };
    default:
      return {
        name: op.shipEntrepotName,
        street1: op.shipAdress,
        street2: op.shipAdress2,
        zip: op.shipPostalCode,
        city: op.shipCity,
        country: "FR",
        phone: op?.edb?.phoneContactRecovery,
        email: op?.edb?.emailContactRecovery,
        entityType: ShippingAddressEntityType.WAREHOUSE,
        entityId: op.shipEntrepotName,
      };
  }
};

export const removeDuplicateDeliveryAddresses = (
  deliveryAdresses: any[]
): any[] => {
  const uniqueDeliveryAdresses: any[] = [];
  deliveryAdresses?.forEach((deliveryAddress) => {
    if (
      !uniqueDeliveryAdresses.find(
        (uniqueDeliveryAddress) =>
          uniqueDeliveryAddress.name === deliveryAddress.name
      )
    ) {
      uniqueDeliveryAdresses.push(deliveryAddress);
    }
  });
  return uniqueDeliveryAdresses;
};

export enum ShippingRecordCreationError {
  NO_OP_ERROR,
  LOCATION_ERROR,
  DATE_ERROR,
}
const SHIPPING_RECORD_CREATION_ERROR_MESSAGES = {
  [ShippingRecordCreationError.NO_OP_ERROR]: "Aucun OP sélectionné.",
  [ShippingRecordCreationError.LOCATION_ERROR]:
    "Les OP sélectionnés ont des lieux d'enlèvement différents et ne peuvent pas être regroupés dans le même dossier.",
  [ShippingRecordCreationError.DATE_ERROR]:
    "Les OP sélectionnés ont des dates de mise à disposition incompatibles et ne peuvent pas être regroupés dans le même dossier.",
};
export const getShippingRecordCreationErrorMessage = (
  errorType: ShippingRecordCreationError
): string => SHIPPING_RECORD_CREATION_ERROR_MESSAGES[errorType];

const toStartOfDay = (value: string | Date): Date => {
  const date = value instanceof Date ? value : new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const getOpEdbId = (op: PreparationOrder): string | undefined => {
  const edb = op?.edb as (EDB & { id?: string }) | undefined;
  return (
    edb?._id || edb?.id || (op as PreparationOrder & { edbId?: string }).edbId
  );
};

/**
 * Unique EDB disposition dates (étape 3) across the given OPs.
 * Multiple OPs from the same EDB count as a single date.
 */
export const getUniqueEdbDispositionDates = (
  ops: PreparationOrder[] = []
): Date[] => {
  const datesByEdbId = new Map<string, Date>();
  ops.forEach((op) => {
    const edbId = getOpEdbId(op);
    const dispositionDate = op?.edb?.dispositionDate;
    if (!edbId || !dispositionDate) {
      return;
    }
    if (!datesByEdbId.has(edbId)) {
      datesByEdbId.set(edbId, toStartOfDay(dispositionDate));
    }
  });
  return Array.from(datesByEdbId.values());
};

/**
 * Règle métier — date de livraison souhaitée du dossier :
 * - une seule EDB : date saisie à l'étape 3 de cette EDB
 * - plusieurs EDB :
 *   - si aujourd'hui est antérieure à toutes les dates demandées → date la plus proche d'aujourd'hui
 *   - si aujourd'hui est postérieure à au moins une date demandée → date la plus antérieure parmi celles ≤ aujourd'hui
 */
export const getWishedDeliveryDateFromOps = (
  ops: PreparationOrder[] = [],
  today: Date = new Date()
): Date | null => {
  const uniqueDates = getUniqueEdbDispositionDates(ops);
  if (uniqueDates.length === 0) {
    return null;
  }
  if (uniqueDates.length === 1) {
    return uniqueDates[0];
  }

  const todayStart = toStartOfDay(today);
  const allInFuture = uniqueDates.every(
    (date) => date.getTime() > todayStart.getTime()
  );

  if (allInFuture) {
    return uniqueDates.reduce((closest, date) =>
      date.getTime() < closest.getTime() ? date : closest
    );
  }

  const pastOrTodayDates = uniqueDates.filter(
    (date) => date.getTime() <= todayStart.getTime()
  );
  return pastOrTodayDates.reduce((earliest, date) =>
    date.getTime() < earliest.getTime() ? date : earliest
  );
};

export const getShippingRecordWishedDeliveryDate = (
  shippingRecord?: ShippingRecord | null,
  today: Date = new Date()
): Date | null => {
  const ops =
    shippingRecord?.shipments
      ?.map((shipment) => shipment.op)
      .filter((op): op is PreparationOrder => !!op) || [];
  const fromOps = getWishedDeliveryDateFromOps(ops, today);
  if (fromOps) {
    return fromOps;
  }
  if (!shippingRecord?.wishedDeliveryDate) {
    return null;
  }
  return toStartOfDay(shippingRecord.wishedDeliveryDate);
};

/*
 * Returns a ShippingRecordCreationError if the shipping record cannot be created. Otherwise, returns the wished delivery date.
 */
export const checkIfShippingRecordIsCreatable = (
  ops: PreparationOrder[]
): { deliveryDate?: Date; error?: string } => {
  let canCreateRecord = true;

  // Check if all selected OPs have the same shipping location
  const shippingLocationIdToCheck = ops[0].entrepot._id;
  canCreateRecord = ops.every(
    (selectedOP: PreparationOrder) =>
      selectedOP.entrepot._id === shippingLocationIdToCheck
  );
  if (!canCreateRecord) {
    return {
      error: getShippingRecordCreationErrorMessage(
        ShippingRecordCreationError.LOCATION_ERROR
      ),
    };
  }

  // Check if all selected OPs have the same EXACT disposition date or if the "AT LATEST" disposition date is LATER than the EXACT disposition date
  const firstOPwithExactDate = ops.find(
    (selectedOP: PreparationOrder) => selectedOP.edb?.dispositionDateIsExact
  );
  let error = "";
  if (firstOPwithExactDate) {
    const exactDateToCheck = new Date(
      firstOPwithExactDate.edb.dispositionDate
    ).getTime();
    canCreateRecord = ops.every((selectedOP: PreparationOrder) => {
      const selectedOpDate = new Date(
        selectedOP.edb?.dispositionDate
      ).getTime();
      const isOpDateExact = selectedOP.edb?.dispositionDateIsExact;
      if (isOpDateExact && selectedOpDate !== exactDateToCheck) {
        error = `${selectedOP.code} et ${firstOPwithExactDate.code} ont des dates de mise à disposition exactes qui ne correspondent pas entre elle. Ils ne peuvent pas être regroupés dans le même dossier.`;
        return false;
      }
      if (!isOpDateExact && selectedOpDate < exactDateToCheck) {
        error = `La date de mise disposition "Au plus tard le" de ${selectedOP.code} est antérieure à la date exacte de ${firstOPwithExactDate.code}. Ils ne peuvent pas être regroupés dans le même dossier.`;
        return false;
      }
      return true;
    });
  }
  if (!canCreateRecord) {
    return {
      error:
        error === ""
          ? getShippingRecordCreationErrorMessage(
              ShippingRecordCreationError.DATE_ERROR
            )
          : error,
    };
  }

  const wishedDeliveryDate = getWishedDeliveryDateFromOps(ops);
  if (!wishedDeliveryDate) {
    return {
      error: getShippingRecordCreationErrorMessage(
        ShippingRecordCreationError.DATE_ERROR
      ),
    };
  }
  return {
    deliveryDate: wishedDeliveryDate,
  };
};
